/// <reference types="../../../../env.d.ts" />

import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AppStore,
  FirebaseService,
  StorageService,
  DataService,
  Session,
  Settings,
  normalizeDate,
  ARSettings
} from '@kanek/shared-data-access';
import { ArService } from '@kanektech.com/game-engine';
import { LoadingComponent, AudioService } from '@kanek/shared-ui';
import { LanguageInitializationService } from './services/language-initialization.service';
import { CampaignFlowService } from './services/campaign-flow.service';
import { AlignText } from 'spin-wheel';

/**
 * Interface for campaign short code data structure
 */
interface ShortCodeData {
  id: string;
  name: string;
  gameType?: string;
  arSettings?: ARSettings;
  gameSettings?: Record<string, unknown>;
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    LoadingComponent
  ],
  template: `
    <main>
      <router-outlet />
    </main>

    <lib-loading [loading]="appStore.loading() && !isErrorPage" />
  `,
  styles: `
    main {
      height: 100%;
    }
  `
})
export class AppComponent implements OnInit, OnDestroy  {
  protected appStore = inject(AppStore);
  private router = inject(Router);
  private storage = inject(StorageService);
  private dataService = inject(DataService);
  private firebaseService = inject(FirebaseService);
  private arService = inject(ArService);
  private audioService = inject(AudioService);
  private languageService = inject(LanguageInitializationService);
  private campaignFlowService = inject(CampaignFlowService);

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  // Track if we're on the error page to avoid initialization
  isErrorPage = false;

  private sessionId: string | null = null;
  private short: string | null = null;
  private clientId: string | null = null;

  constructor(
    private route: ActivatedRoute
  ) {
    // Initialize client configuration from environment variables
    console.log('🔍 import.meta.env:', import.meta.env);

    this.clientId = import.meta.env['_NGX_ENV_CLIENT_ID'] || null;

    if (!this.clientId) {
      console.error('❌ CLIENT_ID not found in environment variables. Please ensure _NGX_ENV_CLIENT_ID is set.');
      throw new Error('CLIENT_ID is required but not found in environment configuration');
    }

    console.log('🏗️ Client configuration:', {
      clientId: this.clientId,
      source: 'environment'
    });
  }

  ngOnInit(): void {
    console.log('ngOnInit()');

    // Initialize language FIRST before any rendering
    this.initializeLanguage();

    this.router.events.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(event => {
      // Perform actions that require query parameters here
      if (event instanceof NavigationEnd) {
        // Check if we're on the error page
        this.isErrorPage = event.url.includes('/campaign-error');

        // If on error page, ensure loading is stopped
        if (this.isErrorPage) {
          this.appStore.stopLoading();
        }

        // Apply AR bypass check on every navigation
        this.arService.applyGlobalBypass();

        // Only initialize if not on error page
        if (!this.appStore.ready() && !this.isErrorPage) {
          console.log('Initializing app...');
          const queryParams = this.route.snapshot.queryParamMap;

          if (queryParams.has('s')) {
            this.short = queryParams.get('s');
            console.log('query param s:', this.short);
            // Note: We'll store the short code in localStorage AFTER we check
            // the business logic in init() to respect prize-won restrictions
          }

          if (queryParams.has('i')) {
            this.sessionId = queryParams.get('i');
            console.log('query param i:', this.sessionId);
          }

          this.init();
        }
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Initialize language before any template rendering to prevent ExpressionChangedAfterItHasBeenCheckedError
   */
  private async initializeLanguage(): Promise<void> {
    try {
      console.log('🌐 Initializing language support...');
      await this.languageService.initializeLanguage();
      console.log('✅ Language initialization complete');
    } catch (error) {
      console.error('❌ Language initialization failed:', error);
      // Continue with default language if initialization fails
    }
  }

  async init(): Promise<void> {
    // this.ngUnsubscribe.next();

    this.appStore.startLoading();

    await this.storage.init();

    // check if sessionId exists
    const sessionId = await this.storage.get('sessionId');
    if (sessionId) {
      console.log('sessionId found in localstorage:', sessionId);
      if (this.sessionId && this.sessionId !== sessionId) {
        console.log('ignoring sessionId in query param', this.sessionId);
      }
      this.sessionId = sessionId;
    }

    // check if short exists
    const short = await this.storage.get('short');
    if (short) {
      console.log('short found in localstorage:', short);
      if (this.short && this.short !== short) {
        console.log('stored short is different than short provided in query param', this.short);
      }
    }

    // Use the short code from URL params or storage
    // Note: Campaign guard ensures we have a valid short code before reaching this point
    const shortCode = this.short || short;
    console.log('short code determined:', shortCode, {
      fromUrl: this.short,
      fromStorage: short
    });

    if (!shortCode) {
      console.error('No short code available - this should not happen due to campaign guard');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'missing_short_code',
          message: 'No campaign code was provided in the link.'
        }
      });
      return;
    }

    // Store the short code in localStorage for guard access
    // This happens BEFORE the session call, so guards can access it
    if (shortCode) {
      console.log('🔧 Storing short code in localStorage for guard access:', shortCode);
      this.storage.setSync('short', shortCode);
    }

    // call callable function to get random generated uid
    const getSession = await this.dataService.getSession(this.sessionId, shortCode);
    const session = getSession.data as Session;
    console.log('session:', session);

    // Business Logic Check: The server handles short code updates based on prize status
    // If user hasn't won a prize, server will update session.short to the new shortCode
    // If user has won a prize, server will ignore the new shortCode and keep existing one

    console.log('🔍 Business Logic Check:', {
      providedShortCode: shortCode,
      sessionShortCode: session.short,
      userHasPrize: !!session.prizeWon,
      shortCodeChanged: session.short !== shortCode
    });

    if (session.prizeWon && session.short !== shortCode) {
      console.log('🏆 User has won a prize - server kept existing short code:', session.short);
      console.log('🚫 Ignoring new short code from URL to preserve prize session:', shortCode);
    } else if (session.short !== shortCode) {
      console.log('🔄 No prize won yet - server updated to new short code:', session.short);
    } else {
      console.log('✅ Short code unchanged:', session.short);
    }

    await this.storage.set('sessionId', session.id);
    await this.storage.set('short', session.short);
    // Store the FINAL short code (from session) in localStorage for guard access
    // This ensures guards always use the server-determined short code that respects business logic
    if (session.short) {
      console.log('🔧 Storing final session short code in localStorage:', session.short);
      this.storage.setSync('short', session.short);
    }
    this.appStore.setSession(session);
    if (session.short) {
      this.short = session.short;
    }

    console.log('clear query param');
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });

    // init game settings using multi-tenant architecture
    const settingsResult = await this.dataService.getSettings(this.short, this.clientId || undefined);
    const campaignData = settingsResult.data as Record<string, unknown>; // Campaign data from getCampaign function

    // 🚨 SECURITY: Validate that the campaign belongs to the configured client
    const configuredClientId = import.meta.env['_NGX_ENV_CLIENT_ID'];
    const campaignClientId = campaignData['clientId'] as string;

    console.log('🔒 Security Check:', {
      configuredClientId,
      campaignClientId,
      shortCode: this.short,
      campaignName: campaignData['name']
    });

    if (configuredClientId && campaignClientId && configuredClientId !== campaignClientId) {
      const errorMessage = `🚨 SECURITY VIOLATION: Campaign "${this.short}" belongs to client "${campaignClientId}" but this app is configured for client "${configuredClientId}". Cross-client access denied.`;
      console.error(errorMessage);

      // Navigate to error page or show security error
      this.router.navigate(['/experience/campaign-error'], {
        queryParams: {
          error: 'client_mismatch',
          expected: configuredClientId,
          found: campaignClientId,
          shortCode: this.short
        }
      });
      return;
    }

    console.log('✅ Security Check Passed: Campaign belongs to the correct client');

    // Initialize campaign flow for dynamic step content
    this.campaignFlowService.initializeFlow(campaignData);

    // Get the matching short code to extract game type and settings
    if (!this.short) {
      console.error('❌ Short code is required');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'short_code_missing',
          message: 'Short code is required.',
        }
      });
      return;
    }

    const matchingShortCode = this.findShortCode(campaignData, this.short);
    if (!matchingShortCode) {
      console.error('❌ Matching short code not found');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'short_code_not_found',
          message: 'Short code configuration not found.',
          shortCode: this.short
        }
      });
      return;
    }

    // Extract game type from SHORT CODE (not campaign level)
    const gameType = matchingShortCode.gameType || 'wheel';
    console.log('🎮 Game type from short code:', gameType);

    // Extract game settings from short code
    const gameSettings = matchingShortCode.gameSettings;
    if (!gameSettings) {
      console.error('❌ Game settings not found in short code');
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'settings_missing',
          message: 'Game configuration is missing.',
          shortCode: this.short
        }
      });
      return;
    }

    // Extract settings from campaign data
    const settings: Settings = {
      experience: {
        type: gameType,
        settings: gameSettings, // Store game-specific settings from short code
        ...(campaignData['experience'] as object)
      },
      ...(campaignData['settings'] as object)
    };

    // Load AR pattern data ONLY if the short code has AR enabled
    // This supports both AR-required and non-AR game experiences
    try {
      if (!this.short) {
        throw new Error('Short code is required for campaign initialization');
      }

      // Check if this short code requires AR
      // Backwards compatible: if enabled flag doesn't exist but arSettings does, treat as enabled
      const matchingShortCode = this.findShortCode(campaignData, this.short);
      const requiresAR = matchingShortCode &&
                        matchingShortCode.arSettings &&
                        (matchingShortCode.arSettings.enabled ?? true);

      if (requiresAR) {
        console.log('🎯 Short code requires AR - loading AR pattern data and configuration');

        // Extract full AR settings (pattern + configuration)
        const arSettings = matchingShortCode.arSettings!; // Safe to use ! because requiresAR guarantees it exists
        const patternData = this.extractARPatternFromCampaign(campaignData, this.short);

        // Convert pattern data to data URL for AR.js
        const patternDataUrl = this.createPatternDataUrl(patternData);

        // Apply AR pattern
        settings.patternUrl = patternDataUrl;

        // Apply AR configuration settings
        if (arSettings.patternRatio !== undefined) {
          settings.patternRatio = arSettings.patternRatio;
        }
        if (arSettings.smoothCount !== undefined) {
          settings.smoothCount = arSettings.smoothCount;
        }
        if (arSettings.smoothTolerance !== undefined) {
          settings.smoothTolerance = arSettings.smoothTolerance;
        }
        if (arSettings.smoothThreshold !== undefined) {
          settings.smoothThreshold = arSettings.smoothThreshold;
        }

        // Apply marker size setting
        if (arSettings.size !== undefined) {
          settings.size = arSettings.size;
        }

        // Apply scene scale setting (controls size of 3D scene relative to marker)
        if (arSettings.sceneScale !== undefined) {
          settings.sceneScale = arSettings.sceneScale;
        }

        console.log('✅ AR pattern and configuration loaded successfully:', {
          patternRatio: settings.patternRatio,
          size: settings.size,
          sceneScale: settings.sceneScale,
          smoothCount: settings.smoothCount,
          smoothTolerance: settings.smoothTolerance,
          smoothThreshold: settings.smoothThreshold
        });
      } else {
        console.log('ℹ️ Short code does not require AR - launching direct game experience');
        // No AR pattern needed - game will launch without AR marker detection
      }

    } catch(error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Failed to initialize campaign:', errorMessage);

      // Navigate to error page
      this.router.navigate(['/campaign-error'], {
        queryParams: {
          status: 'initialization_failed',
          message: errorMessage,
          shortCode: this.short
        }
      });
      return;
    }

    // Store processed campaign settings (includes AR config, game settings, prizes, etc.)
    // This replaces the old game-specific stores (wheel-store)
    // Note: 'settings' includes AR pattern config (patternUrl, patternRatio, etc.)
    this.appStore.setCampaign({
      ...settings,
      prizes: campaignData['prizes'] // Include prizes from raw campaign data
    });
    console.log('[App] Campaign data stored in AppStore');

    // Configure audio from campaign settings
    this.configureAudioService(campaignData, gameSettings);

    if (session.prizeWon && session.prizeRevealed) {
      // Prize already won AND revealed
      if (session.contactEmail || session.contactPhone) {
        // User already submitted info
        this.router.navigate(['/experience/redeem']);
      } else {
        // User still needs to submit info
        this.router.navigate(['/experience/win']);
      }
    }

    this.appStore.stopLoading();
    this.appStore.setReady();
  }

  /**
   * Find a short code in the campaign data
   * @param campaignData Campaign data from Firestore
   * @param shortCodeId Short code ID to find
   * @returns Matching short code or null if not found
   */
  private findShortCode(campaignData: Record<string, unknown>, shortCodeId: string): ShortCodeData | null {
    const shortCodes = campaignData['shortCodes'] as ShortCodeData[] | undefined;

    if (!shortCodes || shortCodes.length === 0) {
      console.error('❌ No short codes found in campaign data');
      return null;
    }

    const matchingShortCode = shortCodes.find(sc => sc.id === shortCodeId);

    if (!matchingShortCode) {
      console.error(`❌ Short code "${shortCodeId}" not found in campaign`);
      return null;
    }

    return matchingShortCode;
  }

  /**
   * Extract AR pattern data from campaign short code settings
   * @param campaignData Campaign data from Firestore
   * @param shortCodeId Short code ID to find
   * @returns AR pattern data string
   * @throws Error if pattern data not found or short code doesn't have AR enabled
   */
  private extractARPatternFromCampaign(campaignData: Record<string, unknown>, shortCodeId: string): string {
    const matchingShortCode = this.findShortCode(campaignData, shortCodeId);

    if (!matchingShortCode) {
      throw new Error(`Short code "${shortCodeId}" not found in campaign short codes`);
    }

    // This method should only be called for AR-enabled short codes
    if (!matchingShortCode.arSettings) {
      throw new Error(`Short code "${shortCodeId}" does not have AR enabled`);
    }

    const patternData = matchingShortCode.arSettings.patternData;

    if (!patternData) {
      throw new Error(`AR pattern data not found for AR-enabled short code "${shortCodeId}". Please regenerate the campaign.`);
    }

    // Log pattern metadata for debugging
    const { patternVersion, patternGeneratedAt } = matchingShortCode.arSettings;
    console.log('📦 AR Pattern loaded:', {
      shortCode: shortCodeId,
      size: `${patternData.length} chars`,
      version: patternVersion || 'N/A',
      generatedAt: patternGeneratedAt ? normalizeDate(patternGeneratedAt).toISOString() : 'Unknown'
    });

    return patternData;
  }

  /**
   * Convert AR pattern data string to data URL for AR.js
   * @param patternData The .patt file content
   * @returns Data URL with encoded pattern data
   */
  private createPatternDataUrl(patternData: string): string {
    // AR.js expects a URL it can fetch from
    // Data URL embeds the pattern directly, avoiding network requests
    return `data:text/plain;charset=utf-8,${encodeURIComponent(patternData)}`;
  }

  /**
   * Configure audio service based on campaign and short code settings
   * @param campaignData Campaign data from Firestore
   * @param shortCodeSettings Short code game settings
   */
  private configureAudioService(campaignData: Record<string, unknown>, shortCodeSettings: Record<string, unknown>): void {
    const campaignEnableSFX = campaignData['enableSoundEffects'] !== false;
    const shortCodeEnableSFX = (shortCodeSettings as any).enableSoundEffects !== false;
    const audioEnabled = campaignEnableSFX && shortCodeEnableSFX;
    this.audioService.setEnabled(audioEnabled);
  }
}
